import { useCallback, useEffect } from "react";
import { devWarn } from "@/lib/devLogger";

/**
 * Owns ONLY audio-element playback control for the soundscape: syncing
 * volume/muted onto the <audio> element, pausing on tab-hide (without
 * auto-resuming -- that respects the visitor's explicit choice), and
 * the user-triggered play/pause actions. Split out of useSoundscape.js
 * so this playback logic is independently readable/testable from
 * preference persistence (see useAudioPreferences.js).
 */
export const useAudioControls = ({ audioRef, playing, setPlaying, muted, volume, setHasEntered }) => {
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [audioRef, volume]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [audioRef, muted]);

  // Pause playback when the tab is hidden; do not auto-resume (respects
  // the visitor's explicit choice) -- resumes only via the play button.
  useEffect(() => {
    const onVisibility = () => {
      const audio = audioRef.current;
      if (!audio) return;
      if (document.hidden && !audio.paused) {
        audio.pause();
      } else if (!document.hidden && playing) {
        audio.play().catch((error) => {
          devWarn("Soundscape auto-resume on tab focus was blocked:", error);
        });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [audioRef, playing]);

  const enterWithSound = useCallback(async () => {
    setHasEntered(true);
    const audio = audioRef.current;
    if (!audio) return;
    try {
      await audio.play();
      setPlaying(true);
    } catch (error) {
      devWarn("Soundscape playback could not start (browser autoplay policy):", error);
      setPlaying(false);
    }
  }, [audioRef, setHasEntered, setPlaying]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (playing) {
        audio.pause();
        setPlaying(false);
      } else {
        await audio.play();
        setPlaying(true);
      }
    } catch (error) {
      devWarn("Soundscape play/pause toggle failed:", error);
    }
  }, [audioRef, playing, setPlaying]);

  return { enterWithSound, togglePlay };
};
